
import { getAuth } from "firebase/auth";
import { app } from "../firebase-config.js";
import { TermsAgreementModal } from "../components/auth/TermsAgreementModal.js";
import { SplashScreen } from "../components/auth/SplashScreen.js";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { locale } from "../services/LocaleService.js";

const auth = getAuth(app);
const db = getFirestore(app);

export const LoginPage = async () => {
    const container = document.createElement('div');
    container.className = "relative flex items-center justify-center min-h-screen w-full overflow-hidden";

    // 1. Background (Cinematic)
    // Using bg-black/50 overlay as specified
    container.innerHTML = `
        <div class="absolute inset-0 z-0">
            <img src="/login.webp" class="w-full h-full object-cover" alt="Background">
            <div class="absolute inset-0 bg-black/50"></div>
        </div>

        <!-- Auth Card (Glassmorphism) -->
        <div class="relative z-10 w-full max-w-md p-8 mx-4 backdrop-blur-md bg-white/10 border border-white/20 shadow-2xl rounded-2xl animate-fade-in text-center flex flex-col gap-6" id="auth-container">
            
            <!-- A. Header -->
            <div class="flex flex-col items-center gap-2">
                <img src="/logo.png" alt="SSULWHA" class="w-40 drop-shadow-lg">
                <p class="text-gray-300 text-sm font-medium tracking-wide">${locale.t('login.subtitle')}</p>
            </div>

            <!-- B. Primary Action (Google) -->
            <button id="btn-google-login" class="w-full bg-white text-gray-900 font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors shadow-lg group">
                <img src="https://www.google.com/favicon.ico" class="w-5 h-5 group-hover:scale-110 transition-transform" alt="Google">
                <span>${locale.t('login.btn_google')}</span>
            </button>

            <!-- C. Divider -->
            <div class="flex items-center gap-4 opacity-50">
                <div class="h-px bg-white/30 flex-1"></div>
                <span class="text-xs text-white uppercase tracking-widest font-light">${locale.t('login.or')}</span>
                <div class="h-px bg-white/30 flex-1"></div>
            </div>

            <!-- D. Secondary Action (Email Form) -->
            <form id="form-email-login" class="flex flex-col gap-4 text-left">
                <!-- Email -->
                <div class="space-y-1">
                    <label class="text-xs font-bold text-gray-300 ml-1">${locale.t('login.label_email')}</label>
                    <input type="email" id="input-email" name="email" required 
                        class="relative z-20 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all font-medium"
                        placeholder="name@example.com" autocomplete="email">
                </div>

                <!-- Password -->
                <div class="space-y-1">
                     <label class="text-xs font-bold text-gray-300 ml-1">${locale.t('login.label_password')}</label>
                    <div class="relative">
                        <input type="password" id="input-password" name="password" required 
                            class="relative z-20 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all font-medium pr-10"
                            placeholder="••••••••" autocomplete="current-password">
                        <button type="button" id="btn-toggle-pw" aria-label="Toggle password visibility" class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white z-30">
                            <span class="material-symbols-outlined text-lg" aria-hidden="true">visibility</span>
                        </button>
                    </div>
                </div>

                <!-- Password Confirm (Signup Only) -->
                <div class="space-y-1 hidden" id="pw-confirm-wrapper">
                     <label class="text-xs font-bold text-gray-300 ml-1">${locale.t('login.label_password_confirm') || '비밀번호 확인'}</label>
                    <div class="relative">
                        <input type="password" id="input-password-confirm" name="passwordConfirm" 
                            class="relative z-20 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all font-medium pr-10"
                            placeholder="••••••••" autocomplete="new-password">
                        <button type="button" id="btn-toggle-pw-confirm" aria-label="Toggle password confirmation visibility" class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white z-30">
                            <span class="material-symbols-outlined text-lg" aria-hidden="true">visibility</span>
                        </button>
                    </div>
                </div>

                <!-- Action Btn -->
                <button type="submit" id="btn-email-submit" class="mt-2 w-full bg-violet-600/80 hover:bg-violet-600 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg border border-violet-500/50">
                    ${locale.t('login.btn_email_login')}
                </button>
            </form>
            
            <!-- E. Toggle Mode -->
            <p class="text-xs text-gray-400">
                ${locale.t('login.msg_no_account')} 
                <button id="btn-toggle-mode" class="text-violet-300 hover:text-white font-bold underline underline-offset-2 ml-1 cursor-pointer relative z-20">${locale.t('login.btn_signup')}</button>
            </p>

            <div id="auth-error-msg" class="hidden text-xs text-red-400 bg-red-900/20 border border-red-500/30 p-3 rounded-lg text-left flex items-start gap-2">
                <span class="material-symbols-outlined text-sm mt-0.5">error</span>
                <span id="auth-error-text">${locale.t('login.err_default')}</span>
            </div>
        </div>
    `;

    // Logic
    setTimeout(() => {
        const btnGoogle = container.querySelector('#btn-google-login');
        const form = container.querySelector('#form-email-login');
        const inputEmail = container.querySelector('#input-email');
        const inputPw = container.querySelector('#input-password');
        const inputPwConfirm = container.querySelector('#input-password-confirm');
        const pwConfirmWrapper = container.querySelector('#pw-confirm-wrapper');
        const btnTogglePw = container.querySelector('#btn-toggle-pw');
        const btnTogglePwConfirm = container.querySelector('#btn-toggle-pw-confirm');
        const btnSubmit = container.querySelector('#btn-email-submit');
        const btnToggleMode = container.querySelector('#btn-toggle-mode');
        const errorBox = container.querySelector('#auth-error-msg');
        const errorText = container.querySelector('#auth-error-text');

        // Z-Index Fix Check (Logged in Logic)
        // Already added relative z-20 to inputs.

        let isLoginMode = true;

        const showError = (msg) => {
            errorText.textContent = msg;
            errorBox.classList.remove('hidden');
        };

        const checkTermsAndRedirect = async (user) => {
            const userRef = doc(db, "users", user.uid);

            // [FIX] Race Condition Prevention: Wait for auth.js to create the user document.
            // Do NOT create the document here — auth.js is the single owner of user doc creation.
            // Previously, both login.js and auth.js tried to create the doc simultaneously,
            // causing Firestore permission-denied errors for new users.
            let snap;
            for (let i = 0; i < 20; i++) {
                snap = await getDoc(userRef);
                if (snap.exists()) break;
                if (i < 19) await new Promise(r => setTimeout(r, 500));
            }

            if (!snap || !snap.exists()) {
                console.error("[Login] User document not created after timeout. Redirecting to home.");
                window.location.hash = '/';
                return;
            }

            // Check Agreement
            if (snap.data().terms_agreed) {
                // [FIX] Retention Logic: Check for pending redirect
                const pendingRedirect = sessionStorage.getItem('auth_redirect');
                if (pendingRedirect) {
                    console.log("[Login] Executing Retention Redirect:", pendingRedirect);
                    sessionStorage.removeItem('auth_redirect');
                    window.router.navigate(pendingRedirect);
                } else {
                    if (window.location.hash.includes('/login') || window.location.hash === '') {
                        window.location.hash = '/';
                    }
                }
                return;
            }

            // Show Terms Modal
            new TermsAgreementModal(user, () => {
                // [FIX] Retention Logic (Post-Terms)
                const pendingRedirect = sessionStorage.getItem('auth_redirect');
                if (pendingRedirect) {
                    sessionStorage.removeItem('auth_redirect');
                    window.router.navigate(pendingRedirect);
                } else {
                    if (window.location.hash.includes('/login') || window.location.hash === '') {
                        window.location.hash = '/';
                    }
                }
            }).render();
        };

        // Google Login
        btnGoogle.onclick = async () => {
            try {
                // 1. Trigger Google Auth
                await window.auth.loginWithGoogle();

                // 2. Authentication Successful - Show Splash Screen
                if (auth.currentUser) {
                    // Hide existing login form
                    const authContainer = container.querySelector('#auth-container');
                    if (authContainer) authContainer.classList.add('opacity-0', 'pointer-events-none', 'transition-opacity', 'duration-500');

                    // Show Cinematic Splash
                    const splash = new SplashScreen();
                    splash.render();

                    // 3. Process User Data (Terms Check)
                    // Artificial delay for UX (1.5s) + Processing time
                    const minDelay = new Promise(resolve => setTimeout(resolve, 2000));

                    try {
                        await Promise.all([
                            checkTermsAndRedirect(auth.currentUser), // This redirects on success
                            minDelay
                        ]);

                        // If checkTermsAndRedirect didn't redirect (meaning modal shown), splash might be in the way.
                        // But checkTermsAndRedirect either redirects (success) or shows Modal.
                        // If it shows Modal, we should probably destroy Splash?
                        // "The Gatekeeper" - if terms are not agreed, we need to show the modal.
                        // Currently checkTermsAndRedirect does:
                        // if agreed -> hash = '/'
                        // if not -> new TermsAgreementModal...

                        // If we are showing the modal, we need to remove the splash screen.
                        const userRef = doc(db, "users", auth.currentUser.uid);
                        const snap = await getDoc(userRef);

                        if (!snap.exists() || !snap.data().terms_agreed) {
                            splash.destroy(); // Remove splash to show Terms Modal
                        } else {
                            // Agreed. Transitions to main page.
                            // The hash change in checkTermsAndRedirect will trigger router.
                            // Router will re-render. Splash is fixed on body, so it persists until re-render triggers or we remove it.
                            // Ideally, router clears body or splash is z-index high.
                            // We should let the splash fade out ON the main page?
                            // Or just simple redirect.

                            // For smoothness:
                            splash.destroy(); // Fade out while moving
                        }

                    } catch (innerErr) {
                        console.error("Terms check failed", innerErr);
                        splash.destroy();
                        showError(innerErr.message);
                        if (authContainer) authContainer.classList.remove('opacity-0', 'pointer-events-none');
                    }
                }

            } catch (e) {
                // [FIX] Ignore Popup Cancellation Errors
                if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
                    console.warn("Google Login Cancelled:", e.code);
                    return; // Do not show error box
                }
                console.error(e);
                showError(e.message);
            }
        };

        // Toggle Password
        btnTogglePw.onclick = (e) => {
            e.preventDefault();
            if (inputPw.type === 'password') {
                inputPw.type = 'text';
                btnTogglePw.innerHTML = '<span class="material-symbols-outlined text-lg">visibility_off</span>';
            } else {
                inputPw.type = 'password';
                btnTogglePw.innerHTML = '<span class="material-symbols-outlined text-lg">visibility</span>';
            }
        };

        // Toggle Password Confirm
        btnTogglePwConfirm.onclick = (e) => {
            e.preventDefault();
            if (inputPwConfirm.type === 'password') {
                inputPwConfirm.type = 'text';
                btnTogglePwConfirm.innerHTML = '<span class="material-symbols-outlined text-lg">visibility_off</span>';
            } else {
                inputPwConfirm.type = 'password';
                btnTogglePwConfirm.innerHTML = '<span class="material-symbols-outlined text-lg">visibility</span>';
            }
        };

        // Toggle Mode
        btnToggleMode.onclick = () => {
            isLoginMode = !isLoginMode;
            if (isLoginMode) {
                btnSubmit.textContent = locale.t('login.btn_email_login');
                btnToggleMode.textContent = locale.t('login.btn_signup');
                const p = container.querySelector('p.text-xs.text-gray-400');
                if (p) p.childNodes[0].textContent = locale.t('login.msg_no_account') + " ";
                inputPw.autocomplete = "current-password";
                pwConfirmWrapper.classList.add('hidden');
                inputPwConfirm.removeAttribute('required');
                errorBox.classList.add('hidden');
            } else {
                btnSubmit.textContent = locale.t('login.btn_email_signup');
                btnToggleMode.textContent = locale.t('login.btn_login');
                const p = container.querySelector('p.text-xs.text-gray-400');
                if (p) p.childNodes[0].textContent = locale.t('login.msg_has_account') + " ";
                inputPw.autocomplete = "new-password";
                pwConfirmWrapper.classList.remove('hidden');
                inputPwConfirm.setAttribute('required', '');
                errorBox.classList.add('hidden');
            }
        };

        // Submit
        form.onsubmit = async (e) => {
            e.preventDefault();
            const email = inputEmail.value;
            const password = inputPw.value;
            showError(locale.t('login.msg_processing'));
            errorBox.classList.add('hidden'); // Hide until actual error

            try {
                if (isLoginMode) {
                    await window.auth.login(email, password);
                } else {
                    if (password !== inputPwConfirm.value) {
                        showError(locale.t('login.err_password_mismatch') || '비밀번호가 일치하지 않습니다.');
                        return;
                    }
                    await window.auth.signUp(email, password);
                    await window.Modal.alert(locale.t('login.msg_email_sent'), locale.t('login.msg_signup_success') || '가입 완료');
                    isLoginMode = true;
                    btnToggleMode.click();
                    return;
                }

                if (auth.currentUser) {
                    await checkTermsAndRedirect(auth.currentUser);
                }

            } catch (err) {
                console.error(err);
                if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
                    showError(locale.t('login.err_invalid_auth'));
                } else if (err.code === 'auth/email-already-in-use') {
                    showError(locale.t('login.err_email_in_use'));
                } else {
                    showError(err.message || locale.t('login.err_default'));
                }
            }
        };

    }, 0);

    return container;
};
